import React, { useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import TitlePage from "../../../../components/TitlePage";
import { Waybill } from "../../../../models/waybill";
import { ShipmentStatus } from "../../../../util/enums";
import { uniqueArray } from "../../../../util/uniqueArray";
import WaybillGrid from "./components/Grid";
import DeleteWaybillModal, { DeleteWaybillHandle } from "./components/Modal";
import SendToCustomerModal, { SendToCustomerHandle } from "./components/SendCommunicationsModal";
import WaybillView, { WaybillViewHandle } from "./components/View";
import { useActions } from "./hooks/useAction";
import { ShipmentProvider } from "./Maintenance/context/useContext";
import { DataToShareLink } from "./types";
import { Breadcrumbs } from "@mui/material";
import { NavigateNext } from "@mui/icons-material";
import useStyles, { Wrapper } from "./styles";

export default function Waybills() {
  const { t: translate } = useTranslation();

  const classes = useStyles();
  const deleteWaybillModalRef = useRef<DeleteWaybillHandle>(null);
  const shareLinkModalRef = useRef<SendToCustomerHandle>(null);
  const waybillViewRef = useRef<WaybillViewHandle>(null);
  const [shipmentEmails, setShipmentEmails] = useState<string[]>([]);
  const [shipmentPhones, setShipmentPhones] = useState<string[]>([]);

  const { useUpdateWaybill } = useActions();
  const { mutate: updateWaybill, isLoading: isDeleting, isSuccess } = useUpdateWaybill();

  const onOpenConfirmationModal = (waybill?: Waybill) => {
    if (waybill) deleteWaybillModalRef?.current?.onOpenModal(waybill);
    else deleteWaybillModalRef?.current?.onCloseModal();
  };

  const onDelete = (waybill?: Waybill) => {
    if (waybill) {
      updateWaybill({
        id: Number(waybill.id),
        waybill: {
          ...waybill,
          statusShipment: ShipmentStatus.CANCELLED,
          marineTrafficId: null,
          shipmentTrackingId: null,
        },
      });
    }
  };

  const onSendShareLink = (data: DataToShareLink) => {
    shareLinkModalRef.current?.setShipmentId(data.id);
    shareLinkModalRef.current?.setContainerId(data.reference || "");
    setShipmentEmails(uniqueArray(data.emails));
    setShipmentPhones(uniqueArray(data.phones));
    shareLinkModalRef.current?.setOpen(true);
  };

  const renderHeader = () => {
    return (
      <Wrapper className={classes.operationWrapper} hasBoxShadow={true}>
        <Breadcrumbs
          data-cy="breadcrumbs"
          separator={<NavigateNext />}
          aria-label="breadcrumb"
          className={classes.operationHeaderBreadcrumb}
        >
          <span data-cy="breadcrumbsTitle" className={classes.operationHeaderBreadcrumbTitle}>
            {translate("operations")}
          </span>
          <span
            data-cy="breadcrumbsSubtitle"
            className={`${classes.operationHeaderBreadcrumbSubTitle}`}
          >
            {translate("shipments")}
          </span>
        </Breadcrumbs>
      </Wrapper>
    );
  };

  return (
    <div data-cy="waybills-page">
      {/* {renderHeader()} */}

      <WaybillGrid
        onConfirmationModal={onOpenConfirmationModal}
        onOpenViewComponent={(id) => waybillViewRef.current?.onOpenView(id)}
        onShareLink={onSendShareLink}
        wasDeletingSuccessfully={isSuccess}
      />

      <SendToCustomerModal
        ref={shareLinkModalRef}
        shipmentPhones={shipmentPhones}
        shipmentEmails={shipmentEmails}
      />

      <DeleteWaybillModal ref={deleteWaybillModalRef} onDelete={onDelete} isDeleting={isDeleting} />

      <ShipmentProvider>
        <WaybillView ref={waybillViewRef} />
      </ShipmentProvider>
    </div>
  );
}
